import React, { useState, useEffect, useRef, useCallback } from 'react';
import SEO from '../components/SEO';
import { Link } from 'react-router-dom';
import useBrowseCategories from '../hooks/useBrowseCategories';
import { Row, Col, Typography, Button, Statistic, Space, Modal, message } from 'antd';
import CountUp from 'react-countup';
import {
    HeartOutlined,
    UserOutlined,
    CheckCircleOutlined,
    TrophyOutlined,
    SafetyCertificateOutlined,
    GlobalOutlined,
    StarOutlined,
    RocketOutlined,
    MessageOutlined,
    ThunderboltOutlined,
    ArrowRightOutlined,
    TeamOutlined,
    SearchOutlined,
    CustomerServiceOutlined,
    ShopOutlined,
    CrownOutlined,
    CloudUploadOutlined,
    FilePdfOutlined,
    FileExcelOutlined,
    FileImageOutlined,
    FileOutlined,
    DeleteOutlined,
    ReadOutlined,
    EnvironmentOutlined,
    CheckCircleFilled,
    CloseCircleOutlined,
    InfoCircleOutlined,
} from '@ant-design/icons';
import LandingNavbar from '../layouts/Navbar';
import LandingFooter from '../layouts/Footer';
import BiodataTourHint from '../components/BiodataTourHint';
import { useSponsor } from '../context/SponsorContext';
import api from '../api/axios';



const { Title, Text, Paragraph } = Typography;

/* ──────────────────────────────────────────────────
   Animated statistic with CountUp
   ────────────────────────────────────────────────── */
const AnimatedStatistic = ({ title, value, delay = 0, suffix = '' }) => (
    <Statistic
        title={
            <span style={{ fontSize: '0.95rem', fontWeight: '500', color: '#64748B' }}>
                {title}
            </span>
        }
        value={value}
        suffix={suffix}
        valueStyle={{
            background: 'linear-gradient(135deg, #AD1457, #D81B60)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            fontSize: '2.6rem',
            fontWeight: '700',
            lineHeight: 1.1,
        }}
        formatter={(v) => (
            <CountUp end={v} duration={2.2} delay={delay} separator="," decimals={0} />
        )}
    />
);

/* ──────────────────────────────────────────────────
   Section wrapper with animation on scroll
   ────────────────────────────────────────────────── */
const AnimatedSection = ({ children, delay = 0, style = {} }) => {
    const ref = useRef(null);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect(); } },
            { threshold: 0.12 }
        );
        if (ref.current) observer.observe(ref.current);
        return () => observer.disconnect();
    }, []);

    return (
        <div
            ref={ref}
            style={{
                ...style,
                opacity: visible ? 1 : 0,
                transform: visible ? 'translateY(0)' : 'translateY(28px)',
                transition: `opacity 0.65s ease ${delay}s, transform 0.65s ease ${delay}s`,
            }}
        >
            {children}
        </div>
    );
};

/* ──────────────────────────────────────────────────
   Sponsor Carousel — Jeweller Partners
   Pay-for-visibility: higher tier = longer display time per cycle
   ────────────────────────────────────────────────── */

const SponsorCarousel = ({ isMobile, sponsors }) => {
    const [current, setCurrent] = useState(0);
    const [progressKey, setProgressKey] = useState(0);
    const timerRef = useRef(null);

    const goTo = useCallback((index) => {
        setCurrent(index);
        setProgressKey((k) => k + 1);
    }, []);

    const goNext = useCallback(() => {
        goTo((prev) => (prev + 1) % sponsors.length);
    }, [goTo, sponsors.length]);

    useEffect(() => {
        setCurrent(0);
        setProgressKey((k) => k + 1);
    }, [sponsors]);

    useEffect(() => {
        if (!sponsors.length) return;
        timerRef.current = setTimeout(goNext, sponsors[current].duration * 1000);
        return () => clearTimeout(timerRef.current);
    }, [current, goNext, sponsors]);

    const BANNER_H = isMobile ? 200 : 800;

    return (
        <div style={{ marginTop: isMobile ? '48px' : '53px', position: 'relative', zIndex: 10, lineHeight: 0 }}>

            {/* ── Slides ── */}
            <div style={{ position: 'relative', height: BANNER_H, overflow: 'hidden' }}>
                {sponsors.map((sp, idx) => (
                    <div
                        key={sp.id}
                        style={{
                            position: 'absolute', inset: 0,
                            opacity: idx === current ? 1 : 0,
                            transition: 'opacity 0.7s ease',
                            pointerEvents: idx === current ? 'auto' : 'none',
                        }}
                    >
                        {sp.image_url ? (
                            <img
                                src={sp.image_url}
                                alt="sponsor"
                                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                            />
                        ) : (
                            /* Placeholder until sponsor uploads image */
                            <div style={{
                                width: '100%', height: '100%', position: 'relative', overflow: 'hidden',
                                background: sp.tier_gradient,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                                <div style={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(rgba(0,0,0,0.04) 1px, transparent 1px)', backgroundSize: '22px 22px' }} />
                                <div style={{ position: 'absolute', right: '-40px', top: '-40px', width: '180px', height: '180px', borderRadius: '50%', background: 'rgba(255,255,255,0.18)' }} />
                                <div style={{ position: 'absolute', left: '-25px', bottom: '-25px', width: '120px', height: '120px', borderRadius: '50%', background: 'rgba(255,255,255,0.12)' }} />
                                <div style={{ textAlign: 'center', position: 'relative', zIndex: 1, padding: '0 24px', lineHeight: 1.4 }}>
                                    <ShopOutlined style={{ fontSize: '2rem', color: 'rgba(255,255,255,0.9)', opacity: 0.4, display: 'block', marginBottom: '10px' }} />
                                    <div style={{ color: 'rgba(255,255,255,0.9)', fontWeight: '700', fontSize: isMobile ? '1rem' : '1.3rem', marginBottom: '6px' }}>{sp.brand_name}</div>
                                    <div style={{ display: 'inline-block', background: 'rgba(255,255,255,0.4)', borderRadius: '6px', padding: '3px 14px', fontSize: '0.72rem', color: 'rgba(255,255,255,0.9)', fontWeight: '600' }}>
                                        {isMobile ? 'Your ad here' : 'Place your banner image here · 1200 × 220 px recommended'}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                ))}

                {/* Prev / Next arrows */}
                {['prev', 'next'].map((dir) => (
                    <button
                        key={dir}
                        onClick={dir === 'prev'
                            ? () => goTo((current - 1 + sponsors.length) % sponsors.length)
                            : goNext}
                        style={{
                            position: 'absolute', top: '50%', transform: 'translateY(-50%)',
                            [dir === 'prev' ? 'left' : 'right']: '14px',
                            width: '36px', height: '36px', borderRadius: '50%',
                            background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.2)',
                            color: 'white', cursor: 'pointer', zIndex: 10,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: '1.2rem', lineHeight: 1, transition: 'all 0.2s ease',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(0,0,0,0.6)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(0,0,0,0.3)'; }}
                    >
                        {dir === 'prev' ? '‹' : '›'}
                    </button>
                ))}

                {/* Dot indicators — overlaid at bottom of image */}
                <div style={{
                    position: 'absolute', bottom: '10px', left: 0, right: 0,
                    display: 'flex', justifyContent: 'center', gap: '6px', zIndex: 10,
                }}>
                    {sponsors.map((sp, idx) => (
                        <button
                            key={sp.id}
                            onClick={() => goTo(idx)}
                            style={{
                                width: idx === current ? '22px' : '7px',
                                height: '7px', borderRadius: '9999px', border: 'none',
                                background: idx === current ? 'white' : 'rgba(255,255,255,0.45)',
                                cursor: 'pointer', padding: 0,
                                transition: 'all 0.3s ease',
                                boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
                            }}
                        />
                    ))}
                </div>

            </div>

            {/* Thin progress bar just below the image */}
            <div style={{ height: '3px', background: 'rgba(0,0,0,0.08)', overflow: 'hidden' }}>
                <div
                    key={progressKey}
                    style={{
                        height: '100%',
                        background: sponsors[current].tierGradient,
                        animation: `sponsorFill ${sponsors[current].duration}s linear forwards`,
                    }}
                />
            </div>

            <style>{`
                @keyframes sponsorFill { from { width: 0% } to { width: 100% } }
            `}</style>
        </div>
    );
};

/* ──────────────────────────────────────────────────
   Biodata upload helpers
   ────────────────────────────────────────────────── */
const BIODATA_ACCEPTED = {
    'application/pdf': { label: 'PDF', color: '#EF4444' },
    'application/msword': { label: 'DOC', color: '#3B82F6' },
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': { label: 'DOCX', color: '#3B82F6' },
    'application/vnd.ms-excel': { label: 'XLS', color: '#10B981' },
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': { label: 'XLSX', color: '#10B981' },
    'image/jpeg': { label: 'JPG', color: '#8B5CF6' },
    'image/png': { label: 'PNG', color: '#8B5CF6' },
    'text/plain': { label: 'TXT', color: '#64748B' },
};
const BIODATA_ACCEPT = '.pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.txt';
const BIODATA_MAX_MB = 2;

const bioFileIcon = (type) => {
    if (type === 'application/pdf') return <FilePdfOutlined style={{ fontSize: '2rem' }} />;
    if (type?.includes('image')) return <FileImageOutlined style={{ fontSize: '2rem' }} />;
    if (type?.includes('excel') || type?.includes('spreadsheet')) return <FileExcelOutlined style={{ fontSize: '2rem' }} />;
    return <FileOutlined style={{ fontSize: '2rem' }} />;
};

const fmtSize = (b) => b < 1024 * 1024
    ? `${(b / 1024).toFixed(1)} KB`
    : `${(b / (1024 * 1024)).toFixed(2)} MB`;

/* ──────────────────────────────────────────────────
   Browse Profiles By — tab visual config
   ────────────────────────────────────────────────── */
const TAB_CONFIG = [
    { key: 'community',  label: 'Community',  icon: <TeamOutlined />,        param: 'community' },
    { key: 'education',  label: 'Education',  icon: <ReadOutlined />,        param: 'education' },
    { key: 'location',   label: 'Location',   icon: <EnvironmentOutlined />, param: 'city' },
    { key: 'profession', label: 'Profession', icon: <ShopOutlined />,      param: 'occupation' },
];

const HeroBrowsePanel = () => {
    const { categories } = useBrowseCategories();
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return (
        <div>
            <Row gutter={isMobile ? [16, 16] : [24, 24]}>
                {TAB_CONFIG.map(tab => {
                    const items = (categories[tab.key] ?? []).slice(0, 12);
                    return (
                        <Col xs={12} md={6} key={tab.key}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                                {React.cloneElement(tab.icon, { style: { color: '#AD1457', fontSize: '1rem' } })}
                                <span style={{ fontWeight: 700, fontSize: '0.95rem', color: '#1A1A2E' }}>
                                    {tab.label}
                                </span>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                {items.map(item => (
                                    <Link
                                        key={item.label}
                                        to={`/profiles?${tab.param}=${encodeURIComponent(item.label)}`}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            fontSize: '0.92rem',
                                            color: '#475569',
                                            textDecoration: 'none',
                                            transition: 'all 0.15s ease',
                                            padding: '4px 0',
                                        }}
                                        onMouseEnter={(e) => { e.currentTarget.style.color = '#AD1457'; e.currentTarget.style.paddingLeft = '4px'; }}
                                        onMouseLeave={(e) => { e.currentTarget.style.color = '#475569'; e.currentTarget.style.paddingLeft = '0'; }}
                                    >
                                        <span>{item.label}</span>
                                        <ArrowRightOutlined style={{ fontSize: '0.65rem', opacity: 0.4 }} />
                                    </Link>
                                ))}
                                <Link
                                    to={`/profiles?browse=${tab.key}`}
                                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                                    style={{
                                        color: '#AD1457',
                                        fontSize: '0.82rem',
                                        fontWeight: 600,
                                        textDecoration: 'none',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '5px',
                                        marginTop: '8px',
                                        transition: 'gap 0.15s ease',
                                    }}
                                    onMouseEnter={(e) => { e.currentTarget.style.gap = '9px'; }}
                                    onMouseLeave={(e) => { e.currentTarget.style.gap = '5px'; }}
                                >
                                    View all <ArrowRightOutlined style={{ fontSize: '0.68rem' }} />
                                </Link>
                            </div>
                        </Col>
                    );
                })}
            </Row>
        </div>
    );
};

/* ──────────────────────────────────────────────────
   Main Landing component
   ────────────────────────────────────────────────── */
const Landing = () => {
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const isMobile = windowWidth < 768;
    const { sponsorData } = useSponsor();

    const carouselEnabled = sponsorData?.enabled && Array.isArray(sponsorData?.ad_spots) && sponsorData.ad_spots.length > 0;
    const adSpots = carouselEnabled ? sponsorData.ad_spots : [];

    useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const isDesktop = windowWidth >= 992;

    /* ── Biodata modal state ── */
    const [modalOpen, setModalOpen] = useState(false);
    const [modalFile, setModalFile] = useState(null);
    const [modalDropping, setModalDropping] = useState(false);
    const [modalUploading, setModalUploading] = useState(false);
    const [modalStatus, setModalStatus] = useState(null);
    const [modalError, setModalError] = useState('');
    const modalInputRef = useRef();

    /* ── Biodata section state ── */
    const [secFile, setSecFile] = useState(null);
    const [secDropping, setSecDropping] = useState(false);
    const [secUploading, setSecUploading] = useState(false);
    const [secStatus, setSecStatus] = useState(null);
    const [secError, setSecError] = useState('');
    const secInputRef = useRef();

    const validateBio = (f) => {
        if (!BIODATA_ACCEPTED[f.type]) {
            message.error('Unsupported format. Upload PDF, Word, Excel, JPG, or PNG.');
            return false;
        }
        if (f.size > BIODATA_MAX_MB * 1024 * 1024) {
            message.error(`File too large. Max ${BIODATA_MAX_MB} MB.`);
            return false;
        }
        return true;
    };

    const uploadBio = async (file, setUploading, setStatus, setError, setFile) => {
        setUploading(true);
        setStatus(null);
        const formData = new FormData();
        formData.append('biodata', file);
        try {
            await api.post('/biodata/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
            setStatus('success');
            setFile(null);
        } catch (err) {
            const msg = err.response?.data?.message;
            setError(typeof msg === 'object' ? Object.values(msg).flat().join(' ') : (msg || 'Upload failed. Please try again.'));
            setStatus('error');
        } finally {
            setUploading(false);
        }
    };

    /* ── Open modal whenever a file is dragged anywhere on the page ──
         Capture phase ensures we fire even if a child element calls
         stopPropagation (e.g. the navbar). Counter-free: modal only
         closes when the user explicitly dismisses it or uploads.     ── */
    useEffect(() => {
        const onEnter = (e) => {
            if ([...e.dataTransfer.types].includes('Files')) setModalOpen(true);
        };
        const onOver = (e) => e.preventDefault();
        const onDrop = (e) => e.preventDefault();
        const opts = { capture: true };
        document.addEventListener('dragenter', onEnter, opts);
        document.addEventListener('dragover',  onOver,  opts);
        document.addEventListener('drop',      onDrop,  opts);
        return () => {
            document.removeEventListener('dragenter', onEnter, opts);
            document.removeEventListener('dragover',  onOver,  opts);
            document.removeEventListener('drop',      onDrop,  opts);
        };
    }, []);

    return (
        <div style={{ fontFamily: 'Calibri, sans-serif', overflowX: 'hidden' }}>
            <SEO
                description="Oottan is India's #1 free matchmaking service. Find your life partner based on religion, caste, education, profession and more. Join millions of verified profiles."
                canonical="/"
            />
            <LandingNavbar />

            {/* ── Global biodata drop modal ── */}
            <Modal
                open={modalOpen}
                onCancel={() => { setModalOpen(false); setModalFile(null); setModalStatus(null); setModalError(''); }}
                footer={null}
                width={isMobile ? '92%' : 520}
                centered
                title={null}
            >
                <div style={{ textAlign: 'center', marginBottom: '20px', paddingTop: '8px' }}>
                    <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '52px', height: '52px', background: 'linear-gradient(135deg,#AD1457,#D81B60)', borderRadius: '14px', marginBottom: '12px' }}>
                        <CloudUploadOutlined style={{ color: 'white', fontSize: '24px' }} />
                    </div>
                    <div style={{ fontWeight: 700, fontSize: '1.15rem', color: '#1a1a2e', fontFamily: 'Calibri, sans-serif' }}>Upload Your Biodata</div>
                    <div style={{ color: '#64748B', fontSize: '0.88rem', marginTop: '4px', fontFamily: 'Calibri, sans-serif' }}>Drop your file below or click to browse</div>
                </div>

                {modalStatus === 'success' ? (
                    <div style={{ textAlign: 'center', padding: '24px 0' }}>
                        <CheckCircleFilled style={{ fontSize: '48px', color: '#10B981', display: 'block', marginBottom: '12px' }} />
                        <div style={{ fontWeight: 700, color: '#1a1a2e', fontSize: '1.1rem', fontFamily: 'Calibri, sans-serif' }}>Biodata Received!</div>
                        <div style={{ color: '#64748B', fontSize: '0.9rem', margin: '6px 0 20px', fontFamily: 'Calibri, sans-serif' }}>Our team will process it and get in touch shortly.</div>
                        <Button onClick={() => { setModalStatus(null); setModalFile(null); }} style={{ borderColor: '#AD1457', color: '#AD1457', borderRadius: '8px', fontFamily: 'Calibri, sans-serif' }}>Upload Another</Button>
                    </div>
                ) : (
                    <>
                        <div
                            onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setModalDropping(true); }}
                            onDragLeave={(e) => { e.stopPropagation(); setModalDropping(false); }}
                            onDrop={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setModalDropping(false);
                                const f = e.dataTransfer.files[0];
                                if (f && validateBio(f)) { setModalFile(f); setModalStatus(null); setModalError(''); }
                            }}
                            onClick={() => !modalFile && modalInputRef.current.click()}
                            style={{
                                border: `2px dashed ${modalDropping ? '#AD1457' : 'rgba(173,20,87,0.25)'}`,
                                borderRadius: '12px', padding: '32px 20px', textAlign: 'center',
                                cursor: modalFile ? 'default' : 'pointer',
                                background: modalDropping ? 'rgba(173,20,87,0.04)' : 'transparent',
                                transition: 'all 0.2s', marginBottom: '14px',
                            }}
                        >
                            <input ref={modalInputRef} type="file" accept={BIODATA_ACCEPT} onChange={(e) => {
                                const f = e.target.files[0];
                                if (f && validateBio(f)) { setModalFile(f); setModalStatus(null); setModalError(''); }
                                e.target.value = '';
                            }} style={{ display: 'none' }} />

                            {modalFile ? (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    <span style={{ color: BIODATA_ACCEPTED[modalFile.type]?.color, flexShrink: 0 }}>{bioFileIcon(modalFile.type)}</span>
                                    <div style={{ flex: 1, textAlign: 'left', minWidth: 0 }}>
                                        <div style={{ fontWeight: 700, color: '#1a1a2e', fontSize: '0.88rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: 'Calibri, sans-serif' }}>{modalFile.name}</div>
                                        <div style={{ color: '#94A3B8', fontSize: '0.78rem', fontFamily: 'Calibri, sans-serif' }}>{BIODATA_ACCEPTED[modalFile.type]?.label} · {fmtSize(modalFile.size)}</div>
                                    </div>
                                    <button onClick={(e) => { e.stopPropagation(); setModalFile(null); setModalStatus(null); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94A3B8', flexShrink: 0 }}>
                                        <DeleteOutlined />
                                    </button>
                                </div>
                            ) : (
                                <>
                                    <CloudUploadOutlined style={{ fontSize: '36px', color: '#AD1457', opacity: modalDropping ? 1 : 0.7, display: 'block', margin: '0 auto 10px' }} />
                                    <div style={{ fontWeight: 600, color: '#1a1a2e', fontSize: '0.9rem', fontFamily: 'Calibri, sans-serif' }}>
                                        {modalDropping ? 'Release to drop' : 'Drop file here or click to browse'}
                                    </div>
                                </>
                            )}
                        </div>

                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', justifyContent: 'center', marginBottom: '10px' }}>
                            {[{ l: 'PDF', c: '#EF4444' }, { l: 'DOC/DOCX', c: '#3B82F6' }, { l: 'XLS/XLSX', c: '#10B981' }, { l: 'JPG/PNG', c: '#8B5CF6' }, { l: 'TXT', c: '#64748B' }].map(({ l, c }) => (
                                <span key={l} style={{ background: `${c}14`, color: c, borderRadius: '6px', padding: '2px 10px', fontSize: '0.72rem', fontWeight: 600, fontFamily: 'Calibri, sans-serif' }}>{l}</span>
                            ))}
                            <span style={{ color: '#94A3B8', fontSize: '0.72rem', alignSelf: 'center', fontFamily: 'Calibri, sans-serif' }}>· Max {BIODATA_MAX_MB} MB · Single file only</span>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', background: 'rgba(173,20,87,0.04)', border: '1px solid rgba(173,20,87,0.12)', borderRadius: '8px', padding: '8px 12px', marginBottom: '14px' }}>
                            <InfoCircleOutlined style={{ color: '#AD1457', fontSize: '13px', marginTop: '2px', flexShrink: 0 }} />
                            <span style={{ color: '#64748B', fontSize: '0.78rem', fontFamily: 'Calibri, sans-serif', lineHeight: 1.5 }}>
                                Your biodata must include your <strong style={{ color: '#1a1a2e' }}>name</strong>, <strong style={{ color: '#1a1a2e' }}>gender</strong>, <strong style={{ color: '#1a1a2e' }}>mobile number</strong>, and <strong style={{ color: '#1a1a2e' }}>email address</strong> — these are required to create your account.
                            </span>
                        </div>

                        {modalStatus === 'error' && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: '8px', padding: '8px 12px', marginBottom: '12px' }}>
                                <CloseCircleOutlined style={{ color: '#EF4444', flexShrink: 0 }} />
                                <span style={{ color: '#EF4444', fontSize: '0.82rem', fontFamily: 'Calibri, sans-serif' }}>{modalError}</span>
                            </div>
                        )}

                        <Button type="primary" block size="large" disabled={!modalFile} loading={modalUploading}
                            onClick={() => uploadBio(modalFile, setModalUploading, setModalStatus, setModalError, setModalFile)}
                            style={{ background: modalFile ? 'linear-gradient(135deg,#AD1457,#D81B60)' : undefined, border: 'none', borderRadius: '10px', fontFamily: 'Calibri, sans-serif', fontWeight: 700, height: '44px' }}
                        >
                            {modalUploading ? 'Uploading…' : 'Upload Biodata'}
                        </Button>
                    </>
                )}
            </Modal>

            {/* ═══════════════════════════════════════
                JEWELLER SPONSOR CAROUSEL
            ═══════════════════════════════════════ */}
            {carouselEnabled && <SponsorCarousel isMobile={isMobile} sponsors={adSpots} />}

            {/* ═══════════════════════════════════════
                HERO SECTION
            ═══════════════════════════════════════ */}
            <div
                className="hero-section"
                style={{
                    minHeight: isMobile ? '95vh' : '90vh',
                    display: 'flex',
                    alignItems: 'center',
                    padding: isMobile ? '80px 20px 60px' : '80px 60px 80px',
                    position: 'relative',
                }}
            >
                {/* Decorative blobs */}
                <div style={{
                    position: 'absolute', top: '10%', right: '5%',
                    width: '320px', height: '320px', borderRadius: '50%',
                    background: 'radial-gradient(circle, rgba(173,20,87,0.1) 0%, transparent 70%)',
                    pointerEvents: 'none',
                }} />
                <div style={{
                    position: 'absolute', bottom: '5%', left: '3%',
                    width: '240px', height: '240px', borderRadius: '50%',
                    background: 'radial-gradient(circle, rgba(139,92,246,0.08) 0%, transparent 70%)',
                    pointerEvents: 'none',
                }} />

                <div style={{ maxWidth: '1280px', margin: '0 auto', width: '100%' }}>
                    <Row justify="center" align="middle" gutter={[48, 40]}>

                        {/* Left — copy */}
                        <Col xs={24} lg={12}>
                            {/* Headline */}
                            <Title
                                className="anim-fadeInLeft delay-200"
                                style={{
                                    color: '#1A1A2E',
                                    fontSize: isMobile ? '2.4rem' : '3.6rem',
                                    fontWeight: '800',
                                    lineHeight: 1.18,
                                    marginBottom: '20px',
                                    letterSpacing: '-0.5px',
                                }}
                            >
                                Find Your{' '}
                                <span className="gradient-text">Perfect Match</span>
                            </Title>

                            <Paragraph
                                className="anim-fadeInLeft delay-300"
                                style={{
                                    color: '#64748B',
                                    fontSize: isMobile ? '1.05rem' : '1.2rem',
                                    marginBottom: '36px',
                                    lineHeight: '1.7',
                                    maxWidth: '480px',
                                }}
                            >
                                Join millions of verified profiles and discover your life partner with confidence, security, and trust.
                            </Paragraph>

                            {/* CTA Buttons */}
                            <div
                                className="anim-fadeInLeft delay-400"
                                style={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: '12px',
                                    justifyContent: isMobile ? 'center' : 'flex-start',
                                }}
                            >
                                <Link to="/signup" style={{ flex: isMobile ? '1 1 0' : '0 0 auto' }}>
                                    <Button
                                        type="primary"
                                        size="large"
                                        icon={<HeartOutlined />}
                                        style={{
                                            height: '52px',
                                            padding: '0 32px',
                                            fontSize: '1rem',
                                            borderRadius: '12px',
                                            fontWeight: '600',
                                            width: isMobile ? '100%' : 'auto',
                                        }}
                                    >
                                        Get Started Free
                                    </Button>
                                </Link>
                                <Link to="/login" style={{ flex: isMobile ? '1 1 0' : '0 0 auto' }}>
                                    <Button
                                        size="large"
                                        style={{
                                            height: '52px',
                                            padding: '0 32px',
                                            fontSize: '1rem',
                                            borderRadius: '12px',
                                            fontWeight: '600',
                                            color: '#AD1457',
                                            border: '2px solid #AD1457',
                                            background: 'transparent',
                                            width: isMobile ? '100%' : 'auto',
                                        }}
                                    >
                                        Sign In
                                    </Button>
                                </Link>
                            </div>

                            {/* Mini trust indicators */}
                            <div
                                className="anim-fadeInLeft delay-500"
                                style={{ display: 'flex', gap: '24px', marginTop: '36px', flexWrap: 'wrap' }}
                            >
                                {[
                                    { icon: <UserOutlined />, text: '1M+ Members' },
                                    { icon: <CheckCircleOutlined />, text: 'Verified Profiles' },
                                    { icon: <StarOutlined />, text: '50K+ Matches' },
                                ].map((item) => (
                                    <div key={item.text} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <span style={{ color: '#AD1457', fontSize: '1rem' }}>{item.icon}</span>
                                        <Text style={{ color: '#64748B', fontSize: '0.88rem', fontWeight: '500' }}>
                                            {item.text}
                                        </Text>
                                    </div>
                                ))}
                            </div>
                        </Col>

                        {/* Right — hero image card */}
                        <Col xs={24} lg={12} style={{ textAlign: 'center' }}>
                            <div className="anim-fadeInRight delay-200" style={{ position: 'relative', display: 'inline-block', width: '100%', maxWidth: '460px' }}>
                                <div
                                    style={{
                                        background: `url(${process.env.REACT_APP_IMAGE_URL}/assets/images/Cartoon_Marriage_Pic_Compressed.png) top/cover no-repeat`,
                                        borderRadius: '24px',
                                        height: isMobile ? '300px' : '420px',
                                        width: '100%',
                                        boxShadow: '0 24px 60px rgba(173,20,87,0.25)',
                                        position: 'relative',
                                        overflow: 'hidden',
                                    }}
                                >
                                    <div style={{
                                        position: 'absolute', inset: 0,
                                        background: 'linear-gradient(135deg, rgba(173,20,87,0.18) 0%, rgba(139,92,246,0.12) 100%)',
                                    }} />
                                </div>

                                {isDesktop && (
                                    <div
                                        className="hero-stat-badge"
                                        style={{
                                            position: 'absolute', top: '20px', right: '-20px',
                                            background: 'white', borderRadius: '14px',
                                            padding: '12px 18px',
                                            boxShadow: '0 8px 32px rgba(173,20,87,0.2)',
                                            display: 'flex', alignItems: 'center', gap: '10px',
                                            animation: 'float 4s ease-in-out infinite',
                                        }}
                                    >
                                        <div style={{
                                            width: '40px', height: '40px', borderRadius: '10px',
                                            background: 'linear-gradient(135deg,#AD1457,#D81B60)',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        }}>
                                            <HeartOutlined style={{ color: 'white', fontSize: '18px' }} />
                                        </div>
                                        <div>
                                            <div style={{ fontWeight: '700', color: '#1A1A2E', fontSize: '1rem', lineHeight: 1.2 }}>50,000+</div>
                                            <div style={{ color: '#64748B', fontSize: '0.75rem' }}>Happy Couples</div>
                                        </div>
                                    </div>
                                )}

                                {isDesktop && (
                                    <div
                                        style={{
                                            position: 'absolute', bottom: '24px', left: '-24px',
                                            background: 'white', borderRadius: '14px',
                                            padding: '12px 18px',
                                            boxShadow: '0 8px 32px rgba(173,20,87,0.2)',
                                            display: 'flex', alignItems: 'center', gap: '10px',
                                            animation: 'float 4s ease-in-out 1.5s infinite',
                                        }}
                                    >
                                        <div style={{
                                            width: '40px', height: '40px', borderRadius: '10px',
                                            background: 'linear-gradient(135deg,#10B981,#34D399)',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        }}>
                                            <CheckCircleOutlined style={{ color: 'white', fontSize: '18px' }} />
                                        </div>
                                        <div>
                                            <div style={{ fontWeight: '700', color: '#1A1A2E', fontSize: '1rem', lineHeight: 1.2 }}>100%</div>
                                            <div style={{ color: '#64748B', fontSize: '0.75rem' }}>Verified Profiles</div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </Col>
                    </Row>
                </div>
            </div>

            {/* ═══════════════════════════════════════
                BROWSE PROFILES BY SECTION
            ═══════════════════════════════════════ */}
            <div style={{ padding: isMobile ? '40px 20px' : '56px 60px', background: 'linear-gradient(180deg,#F8F5FF 0%,#FAFAFE 100%)' }}>
                <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
                    <AnimatedSection style={{ textAlign: 'center', marginBottom: '32px' }}>
                        <Title level={3} style={{ fontSize: isMobile ? '1.4rem' : '1.75rem', fontWeight: 800, color: '#1A1A2E', margin: 0 }}>
                            Browse Profiles
                        </Title>
                        <Text style={{ color: '#64748B', fontSize: '0.97rem' }}>
                            Find your match by community, education, location, or profession
                        </Text>
                    </AnimatedSection>
                    <AnimatedSection delay={0.1}>
                        <HeroBrowsePanel />
                    </AnimatedSection>
                </div>
            </div>

            {/* ═══════════════════════════════════════
                STATS SECTION
            ═══════════════════════════════════════ */}
            <div style={{ padding: isMobile ? '56px 20px' : '80px 60px', background: 'white' }}>
                <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
                    <AnimatedSection style={{ textAlign: 'center', marginBottom: '56px' }}>
                        <div className="section-badge">
                            <TrophyOutlined /> Our Numbers
                        </div>
                        <Title level={2} style={{
                            fontSize: isMobile ? '1.9rem' : '2.5rem',
                            fontWeight: '800', color: '#1A1A2E', marginBottom: '12px',
                        }}>
                            Trusted by Millions Across India
                        </Title>
                        <Text style={{ color: '#64748B', fontSize: '1.05rem' }}>
                            A community built on trust, safety, and lasting relationships
                        </Text>
                    </AnimatedSection>

                    <Row gutter={[24, 24]} justify="center">
                        {[
                            { icon: <UserOutlined />, value: 1000000, suffix: '+', label: 'Active Members', gradient: 'linear-gradient(135deg,#AD1457,#D81B60)', delay: 0 },
                            { icon: <HeartOutlined />, value: 50000, suffix: '+', label: 'Successful Matches', gradient: 'linear-gradient(135deg,#EF4444,#F87171)', delay: 0.1 },
                            { icon: <CheckCircleOutlined />, value: 750000, suffix: '+', label: 'Verified Profiles', gradient: 'linear-gradient(135deg,#10B981,#34D399)', delay: 0.2 },
                        ].map((stat) => (
                            <Col xs={24} sm={8} key={stat.label}>
                                <AnimatedSection delay={stat.delay}>
                                    <div className="stat-card hover-lift">
                                        <div style={{
                                            width: '64px', height: '64px', borderRadius: '16px',
                                            background: stat.gradient,
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            margin: '0 auto 20px',
                                            boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                                        }}>
                                            {React.cloneElement(stat.icon, { style: { fontSize: '1.6rem', color: 'white' } })}
                                        </div>
                                        <AnimatedStatistic
                                            title={stat.label}
                                            value={stat.value}
                                            suffix={stat.suffix}
                                            delay={stat.delay + 0.2}
                                        />
                                    </div>
                                </AnimatedSection>
                            </Col>
                        ))}
                    </Row>
                </div>
            </div>

            {/* ═══════════════════════════════════════
                FEATURES SECTION
            ═══════════════════════════════════════ */}
            <div style={{
                padding: isMobile ? '60px 20px' : '80px 60px',
                background: 'linear-gradient(180deg, #F8F5FF 0%, #FAFAFE 100%)',
            }}>
                <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
                    <AnimatedSection style={{ textAlign: 'center', marginBottom: '56px' }}>
                        <div className="section-badge">
                            <ThunderboltOutlined /> Why Choose Us
                        </div>
                        <Title level={2} style={{
                            fontSize: isMobile ? '1.9rem' : '2.5rem',
                            fontWeight: '800', color: '#1A1A2E', marginBottom: '12px',
                        }}>
                            Why Oottan?
                        </Title>
                        <Text style={{ color: '#64748B', fontSize: '1.05rem' }}>
                            Experience the most reliable and secure way to find your life partner
                        </Text>
                    </AnimatedSection>

                    <Row gutter={[24, 24]}>
                        {[
                            {
                                icon: <TrophyOutlined />,
                                gradient: 'linear-gradient(135deg,#F59E0B,#FCD34D)',
                                title: 'Trusted Platform',
                                desc: "India's most trusted matrimonial service with millions of successful matches and a proven track record of genuine connections.",
                                delay: 0,
                            },
                            {
                                icon: <SafetyCertificateOutlined />,
                                gradient: 'linear-gradient(135deg,#AD1457,#D81B60)',
                                title: 'Verified Profiles',
                                desc: 'Rigorous verification process with document verification and background checks to ensure every profile is genuine.',
                                delay: 0.1,
                            },
                            {
                                icon: <GlobalOutlined />,
                                gradient: 'linear-gradient(135deg,#3B82F6,#60A5FA)',
                                title: 'Wide Reach',
                                desc: 'Connect with matches from all over India and abroad with our extensive network and advanced intelligent search.',
                                delay: 0.2,
                            },
                        ].map((feat) => (
                            <Col xs={24} md={8} key={feat.title}>
                                <AnimatedSection delay={feat.delay} style={{ height: '100%' }}>
                                    <div className="feature-card hover-lift">
                                        <div
                                            className="icon-circle"
                                            style={{ background: feat.gradient, boxShadow: '0 8px 24px rgba(0,0,0,0.15)' }}
                                        >
                                            {React.cloneElement(feat.icon, { style: { fontSize: '1.5rem', color: 'white' } })}
                                        </div>
                                        <Title level={4} style={{ marginBottom: '12px', fontWeight: '700', color: '#1A1A2E' }}>
                                            {feat.title}
                                        </Title>
                                        <Text style={{ fontSize: '0.97rem', lineHeight: '1.7', color: '#64748B' }}>
                                            {feat.desc}
                                        </Text>
                                    </div>
                                </AnimatedSection>
                            </Col>
                        ))}
                    </Row>
                </div>
            </div>

            {/* ═══════════════════════════════════════
                HOW IT WORKS SECTION
            ═══════════════════════════════════════ */}
            <div style={{ padding: isMobile ? '60px 20px' : '80px 60px', background: 'white' }}>
                <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
                    <AnimatedSection style={{ textAlign: 'center', marginBottom: '56px' }}>
                        <div className="section-badge">
                            <RocketOutlined /> Simple & Easy
                        </div>
                        <Title level={2} style={{
                            fontSize: isMobile ? '1.9rem' : '2.5rem',
                            fontWeight: '800', color: '#1A1A2E', marginBottom: '12px',
                        }}>
                            How It Works
                        </Title>
                        <Text style={{ color: '#64748B', fontSize: '1.05rem' }}>
                            Find your perfect partner in three simple steps
                        </Text>
                    </AnimatedSection>

                    <Row gutter={[32, 32]} align="middle">
                        {[
                            {
                                step: '01',
                                icon: <UserOutlined />,
                                title: 'Create Your Profile',
                                desc: 'Sign up and fill in your details — personal, professional, and family background to build a complete profile.',
                                delay: 0,
                            },
                            {
                                step: '02',
                                icon: <SearchOutlined />,
                                title: 'Discover Matches',
                                desc: 'Our smart algorithm recommends the best matches based on your preferences, location, and compatibility.',
                                delay: 0.15,
                            },
                            {
                                step: '03',
                                icon: <MessageOutlined />,
                                title: 'Connect & Meet',
                                desc: 'Send interest, chat securely, and take the next step toward building a lifelong relationship.',
                                delay: 0.3,
                            },
                        ].map((step, index) => (
                            <Col xs={24} md={8} key={step.step}>
                                <AnimatedSection delay={step.delay}>
                                    <div className="step-card hover-lift" style={{ position: 'relative' }}>
                                        {/* Connecting arrow — desktop only */}
                                        {!isMobile && index < 2 && (
                                            <div style={{
                                                position: 'absolute', top: '50px', right: '-28px', zIndex: 2,
                                                color: '#AD1457', fontSize: '1.2rem', opacity: 0.4,
                                            }}>
                                                <ArrowRightOutlined />
                                            </div>
                                        )}
                                        <div className="step-number">{step.step}</div>
                                        <div style={{
                                            width: '52px', height: '52px', borderRadius: '14px',
                                            background: 'var(--color-primary-lighter)',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            margin: '0 auto 16px',
                                        }}>
                                            {React.cloneElement(step.icon, { style: { fontSize: '1.4rem', color: '#AD1457' } })}
                                        </div>
                                        <Title level={4} style={{ marginBottom: '10px', fontWeight: '700', color: '#1A1A2E' }}>
                                            {step.title}
                                        </Title>
                                        <Text style={{ fontSize: '0.95rem', lineHeight: '1.7', color: '#64748B' }}>
                                            {step.desc}
                                        </Text>
                                    </div>
                                </AnimatedSection>
                            </Col>
                        ))}
                    </Row>
                </div>
            </div>

            {/* ═══════════════════════════════════════
                BENEFITS / ADVANCED FEATURES
            ═══════════════════════════════════════ */}
            <div style={{
                padding: isMobile ? '60px 20px' : '80px 60px',
                background: 'linear-gradient(180deg, #F8F5FF 0%, #FAFAFE 100%)',
            }}>
                <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
                    <Row gutter={[48, 40]} align="middle">
                        <Col xs={24} lg={12}>
                            <AnimatedSection>
                                <div className="section-badge">
                                    <StarOutlined /> Premium Features
                                </div>
                                <Title level={2} style={{
                                    fontSize: isMobile ? '1.8rem' : '2.3rem',
                                    fontWeight: '800', color: '#1A1A2E', marginBottom: '28px',
                                }}>
                                    Advanced Features for Better Matching
                                </Title>

                                <div style={{ marginBottom: '32px' }}>
                                    {[
                                        { icon: <ThunderboltOutlined />, text: 'AI-powered compatibility matching' },
                                        { icon: <SafetyCertificateOutlined />, text: 'Privacy-focused secure communication' },
                                        { icon: <CustomerServiceOutlined />, text: '24/7 dedicated customer support' },
                                        { icon: <TeamOutlined />, text: 'Multiple profile management' },
                                    ].map((item) => (
                                        <div
                                            key={item.text}
                                            style={{
                                                display: 'flex', alignItems: 'center', marginBottom: '16px',
                                                background: 'white', borderRadius: '12px', padding: '14px 18px',
                                                border: '1px solid rgba(173,20,87,0.08)',
                                                transition: 'all 0.3s ease',
                                                cursor: 'default',
                                            }}
                                            className="hover-lift"
                                        >
                                            <div style={{
                                                width: '36px', height: '36px', borderRadius: '10px',
                                                background: 'linear-gradient(135deg,#AD1457,#D81B60)',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                marginRight: '14px', flexShrink: 0,
                                                boxShadow: '0 4px 12px rgba(173,20,87,0.25)',
                                            }}>
                                                {React.cloneElement(item.icon, { style: { color: 'white', fontSize: '0.95rem' } })}
                                            </div>
                                            <Text style={{ fontSize: '1rem', color: '#1A1A2E', fontWeight: '500' }}>
                                                {item.text}
                                            </Text>
                                        </div>
                                    ))}
                                </div>

                                <Link
                                    to="/pricing"
                                    style={{
                                        display: 'inline-flex', alignItems: 'center', gap: '8px',
                                        height: '52px', padding: '0 32px',
                                        fontSize: '1rem', borderRadius: '12px', fontWeight: '700',
                                        background: 'linear-gradient(135deg,#AD1457,#D81B60)',
                                        color: 'white', border: 'none', textDecoration: 'none',
                                        boxShadow: '0 6px 20px rgba(173,20,87,0.35)',
                                        transition: 'all 0.25s ease',
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.transform = 'translateY(-2px)';
                                        e.currentTarget.style.boxShadow = '0 10px 28px rgba(173,20,87,0.5)';
                                        e.currentTarget.style.background = 'linear-gradient(135deg,#880E6E,#6B4FD8)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.transform = 'translateY(0)';
                                        e.currentTarget.style.boxShadow = '0 6px 20px rgba(173,20,87,0.35)';
                                        e.currentTarget.style.background = 'linear-gradient(135deg,#AD1457,#D81B60)';
                                    }}
                                >
                                    <ArrowRightOutlined /> Explore Premium Plans
                                </Link>
                            </AnimatedSection>
                        </Col>

                        <Col xs={24} lg={12}>
                            <AnimatedSection delay={0.2}>
                                <div style={{
                                    background: 'linear-gradient(135deg,#AD1457 0%,#D81B60 100%)',
                                    borderRadius: '24px', padding: '48px 36px',
                                    textAlign: 'center', position: 'relative', overflow: 'hidden',
                                    boxShadow: '0 24px 60px rgba(173,20,87,0.3)',
                                }}>
                                    {/* Decorative circle */}
                                    <div style={{
                                        position: 'absolute', top: '-40px', right: '-40px',
                                        width: '180px', height: '180px', borderRadius: '50%',
                                        background: 'rgba(255,255,255,0.07)',
                                    }} />
                                    <div style={{
                                        position: 'absolute', bottom: '-30px', left: '-30px',
                                        width: '130px', height: '130px', borderRadius: '50%',
                                        background: 'rgba(255,255,255,0.05)',
                                    }} />

                                    <div style={{ position: 'relative', zIndex: 1 }}>
                                        <div style={{
                                            width: '80px', height: '80px', borderRadius: '20px',
                                            background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            margin: '0 auto 24px',
                                            border: '1px solid rgba(255,255,255,0.25)',
                                        }}>
                                            <HeartOutlined style={{ fontSize: '2.2rem', color: 'white' }} />
                                        </div>
                                        <Title level={3} style={{ color: 'white', marginBottom: '14px', fontWeight: '700' }}>
                                            Join Our Community
                                        </Title>
                                        <Paragraph style={{ color: 'rgba(255,255,255,0.85)', fontSize: '1.05rem', lineHeight: '1.7', marginBottom: '28px' }}>
                                            Be part of a community where millions have found their perfect match through our trusted platform.
                                        </Paragraph>
                                        <Link to="/signup">
                                            <Button
                                                size="large"
                                                icon={<HeartOutlined />}
                                                style={{
                                                    height: '50px', padding: '0 28px',
                                                    fontSize: '1rem', borderRadius: '12px', fontWeight: '700',
                                                    background: 'linear-gradient(135deg,#FCD34D,#F59E0B)',
                                                    color: '#78350F', border: 'none',
                                                    boxShadow: '0 6px 20px rgba(245,158,11,0.45)',
                                                    transition: 'all 0.25s ease',
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.currentTarget.style.transform = 'translateY(-2px)';
                                                    e.currentTarget.style.boxShadow = '0 10px 28px rgba(245,158,11,0.55)';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.transform = 'translateY(0)';
                                                    e.currentTarget.style.boxShadow = '0 6px 20px rgba(245,158,11,0.45)';
                                                }}
                                            >
                                                Create Free Profile
                                            </Button>
                                        </Link>
                                    </div>
                                </div>
                            </AnimatedSection>
                        </Col>
                    </Row>
                </div>
            </div>

            {/* ═══════════════════════════════════════
                BIODATA UPLOAD SECTION
            ═══════════════════════════════════════ */}
            <div style={{ padding: isMobile ? '60px 20px' : '80px 60px', background: 'white' }}>
                <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
                    <AnimatedSection>
                        <Row gutter={[48, 40]} align="middle">
                            <Col xs={24} lg={12}>
                                <div className="section-badge" style={{ marginBottom: '16px' }}>
                                    <CloudUploadOutlined /> Quick Registration
                                </div>
                                <Title level={2} style={{ fontSize: isMobile ? '1.9rem' : '2.4rem', fontWeight: 800, color: '#1A1A2E', marginBottom: '16px' }}>
                                    Skip the Form —{' '}
                                    <span style={{ background: 'linear-gradient(135deg,#AD1457,#D81B60)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                                        Upload Your Biodata
                                    </span>
                                </Title>
                                <Text style={{ color: '#64748B', fontSize: '1.05rem', lineHeight: 1.7, display: 'block', marginBottom: '24px' }}>
                                    Don't have time to fill out a form? Just upload your biodata and our AI will extract the details to build your profile automatically.
                                </Text>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                    {['AI extracts your details automatically', 'Our team reviews and activates your profile'].map((t) => (
                                        <div key={t} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                            <CheckCircleFilled style={{ color: '#10B981', fontSize: '16px', flexShrink: 0 }} />
                                            <Text style={{ color: '#475569', fontSize: '0.95rem' }}>{t}</Text>
                                        </div>
                                    ))}
                                </div>
                            </Col>

                            <Col xs={24} lg={12}>
                                {secStatus === 'success' ? (
                                    <div style={{ background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.2)', borderRadius: '20px', padding: '48px 32px', textAlign: 'center' }}>
                                        <CheckCircleFilled style={{ fontSize: '52px', color: '#10B981', display: 'block', marginBottom: '14px' }} />
                                        <div style={{ fontWeight: 700, fontSize: '1.2rem', color: '#1a1a2e', fontFamily: 'Calibri, sans-serif', marginBottom: '8px' }}>Biodata Received!</div>
                                        <div style={{ color: '#64748B', fontSize: '0.95rem', fontFamily: 'Calibri, sans-serif', marginBottom: '20px' }}>We'll process it and reach out to you shortly.</div>
                                        <Button onClick={() => { setSecStatus(null); setSecFile(null); }} style={{ borderColor: '#AD1457', color: '#AD1457', borderRadius: '8px', fontFamily: 'Calibri, sans-serif', fontWeight: 600 }}>Upload Another</Button>
                                    </div>
                                ) : (
                                    <div style={{ background: 'linear-gradient(135deg,rgba(173,20,87,0.03),rgba(139,92,246,0.03))', border: '1px solid rgba(173,20,87,0.1)', borderRadius: '20px', padding: '32px' }}>
                                        <div
                                            onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setSecDropping(true); }}
                                            onDragLeave={(e) => { e.stopPropagation(); setSecDropping(false); }}
                                            onDrop={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                setSecDropping(false);
                                                const f = e.dataTransfer.files[0];
                                                if (f && validateBio(f)) { setSecFile(f); setSecStatus(null); setSecError(''); }
                                            }}
                                            onClick={() => !secFile && secInputRef.current.click()}
                                            style={{
                                                border: `2px dashed ${secDropping ? '#AD1457' : 'rgba(173,20,87,0.2)'}`,
                                                borderRadius: '14px', padding: '36px 20px', textAlign: 'center',
                                                cursor: secFile ? 'default' : 'pointer',
                                                background: secDropping ? 'rgba(173,20,87,0.04)' : 'rgba(255,255,255,0.6)',
                                                transition: 'all 0.2s', marginBottom: '16px',
                                            }}
                                        >
                                            <input ref={secInputRef} type="file" accept={BIODATA_ACCEPT} onChange={(e) => {
                                                const f = e.target.files[0];
                                                if (f && validateBio(f)) { setSecFile(f); setSecStatus(null); setSecError(''); }
                                                e.target.value = '';
                                            }} style={{ display: 'none' }} />

                                            {secFile ? (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', textAlign: 'left' }}>
                                                    <span style={{ color: BIODATA_ACCEPTED[secFile.type]?.color, flexShrink: 0 }}>{bioFileIcon(secFile.type)}</span>
                                                    <div style={{ flex: 1, minWidth: 0 }}>
                                                        <div style={{ fontWeight: 700, color: '#1a1a2e', fontSize: '0.9rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: 'Calibri, sans-serif' }}>{secFile.name}</div>
                                                        <div style={{ color: '#94A3B8', fontSize: '0.8rem', fontFamily: 'Calibri, sans-serif' }}>{BIODATA_ACCEPTED[secFile.type]?.label} · {fmtSize(secFile.size)}</div>
                                                    </div>
                                                    <button onClick={(e) => { e.stopPropagation(); setSecFile(null); setSecStatus(null); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94A3B8', flexShrink: 0 }}>
                                                        <DeleteOutlined />
                                                    </button>
                                                </div>
                                            ) : (
                                                <>
                                                    <CloudUploadOutlined style={{ fontSize: '40px', color: '#AD1457', opacity: secDropping ? 1 : 0.65, display: 'block', margin: '0 auto 12px' }} />
                                                    <div style={{ fontWeight: 700, color: '#1a1a2e', fontSize: '0.95rem', marginBottom: '4px', fontFamily: 'Calibri, sans-serif' }}>
                                                        {secDropping ? 'Drop it here!' : 'Drag & drop your biodata here'}
                                                    </div>
                                                    <div style={{ color: '#94A3B8', fontSize: '0.85rem', fontFamily: 'Calibri, sans-serif' }}>
                                                        or <span style={{ color: '#AD1457', fontWeight: 600 }}>click to browse</span>
                                                    </div>
                                                </>
                                            )}
                                        </div>

                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '10px' }}>
                                            {[{ l: 'PDF', c: '#EF4444' }, { l: 'DOC/DOCX', c: '#3B82F6' }, { l: 'XLS/XLSX', c: '#10B981' }, { l: 'JPG/PNG', c: '#8B5CF6' }, { l: 'TXT', c: '#64748B' }].map(({ l, c }) => (
                                                <span key={l} style={{ background: `${c}14`, color: c, borderRadius: '6px', padding: '2px 10px', fontSize: '0.72rem', fontWeight: 600, fontFamily: 'Calibri, sans-serif' }}>{l}</span>
                                            ))}
                                            <span style={{ color: '#94A3B8', fontSize: '0.72rem', alignSelf: 'center', fontFamily: 'Calibri, sans-serif' }}>· Max {BIODATA_MAX_MB} MB · Single file only</span>
                                        </div>

                                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', background: 'rgba(173,20,87,0.04)', border: '1px solid rgba(173,20,87,0.12)', borderRadius: '8px', padding: '8px 12px', marginBottom: '16px' }}>
                                            <InfoCircleOutlined style={{ color: '#AD1457', fontSize: '13px', marginTop: '2px', flexShrink: 0 }} />
                                            <span style={{ color: '#64748B', fontSize: '0.78rem', fontFamily: 'Calibri, sans-serif', lineHeight: 1.5 }}>
                                                Your biodata must include your <strong style={{ color: '#1a1a2e' }}>name</strong>, <strong style={{ color: '#1a1a2e' }}>gender</strong>, <strong style={{ color: '#1a1a2e' }}>mobile number</strong>, and <strong style={{ color: '#1a1a2e' }}>email address</strong> — these are required to create your account.
                                            </span>
                                        </div>

                                        {secStatus === 'error' && (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: '8px', padding: '8px 12px', marginBottom: '12px' }}>
                                                <CloseCircleOutlined style={{ color: '#EF4444', flexShrink: 0 }} />
                                                <span style={{ color: '#EF4444', fontSize: '0.82rem', fontFamily: 'Calibri, sans-serif' }}>{secError}</span>
                                            </div>
                                        )}

                                        <Button type="primary" block size="large" disabled={!secFile} loading={secUploading}
                                            onClick={() => uploadBio(secFile, setSecUploading, setSecStatus, setSecError, setSecFile)}
                                            style={{ background: secFile ? 'linear-gradient(135deg,#AD1457,#D81B60)' : undefined, border: 'none', borderRadius: '10px', fontFamily: 'Calibri, sans-serif', fontWeight: 700, height: '46px' }}
                                        >
                                            {secUploading ? 'Uploading…' : 'Upload Biodata'}
                                        </Button>
                                    </div>
                                )}
                            </Col>
                        </Row>
                    </AnimatedSection>
                </div>
            </div>

            {/* ═══════════════════════════════════════
                CTA SECTION
            ═══════════════════════════════════════ */}
            <div
                className="cta-section"
                style={{
                    background: 'linear-gradient(135deg, #880E6E 0%, #AD1457 50%, #D81B60 100%)',
                    padding: isMobile ? '64px 20px' : '96px 60px',
                    textAlign: 'center',
                }}
            >
                <div style={{ maxWidth: '700px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
                    <AnimatedSection>
                        <div style={{
                            display: 'inline-flex', alignItems: 'center', gap: '8px',
                            background: 'rgba(255,255,255,0.12)', borderRadius: '9999px',
                            padding: '6px 16px', marginBottom: '24px',
                            border: '1px solid rgba(255,255,255,0.2)',
                        }}>
                            <HeartOutlined style={{ color: '#FCD34D', fontSize: '0.9rem' }} />
                            <Text style={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.82rem', fontWeight: '600', letterSpacing: '0.4px' }}>
                                Start Your Love Story Today
                            </Text>
                        </div>

                        <Title level={2} style={{
                            color: 'white', fontWeight: '800', marginBottom: '18px',
                            fontSize: isMobile ? '2rem' : '2.8rem', letterSpacing: '-0.3px',
                        }}>
                            Ready to Find Your Life Partner?
                        </Title>

                        <Paragraph style={{
                            color: 'rgba(255,255,255,0.82)', fontSize: isMobile ? '1rem' : '1.15rem',
                            marginBottom: '40px', lineHeight: '1.7',
                        }}>
                            Join thousands of happy couples who found their perfect match through our platform. Your journey starts with a single step.
                        </Paragraph>

                        <Space size="middle" wrap style={{ justifyContent: 'center', width: '100%' }}>
                            <Link to="/signup" style={{ width: isMobile ? '100%' : '240px' }}>
                                <Button
                                    size="large"
                                    icon={<HeartOutlined />}
                                    style={{
                                        height: '52px', padding: '0 32px',
                                        fontSize: '1.05rem', borderRadius: '12px', fontWeight: '700',
                                        background: 'linear-gradient(135deg,#FCD34D,#F59E0B)',
                                        color: '#78350F', border: 'none',
                                        boxShadow: '0 8px 28px rgba(245,158,11,0.45)',
                                        transition: 'all 0.25s ease',
                                        width: '100%',
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.transform = 'translateY(-2px)';
                                        e.currentTarget.style.boxShadow = '0 12px 32px rgba(245,158,11,0.55)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.transform = 'translateY(0)';
                                        e.currentTarget.style.boxShadow = '0 8px 28px rgba(245,158,11,0.45)';
                                    }}
                                >
                                    Create Free Profile
                                </Button>
                            </Link>
                            <Link
                                to="/pricing"
                                style={{
                                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                                    height: '52px', padding: '0 32px',
                                    fontSize: '1.05rem', borderRadius: '12px', fontWeight: '600',
                                    background: 'transparent', color: 'white', textDecoration: 'none',
                                    border: '2px solid rgba(255,255,255,0.55)',
                                    transition: 'all 0.25s ease',
                                    width: isMobile ? '100%' : '240px',
                                    whiteSpace: 'nowrap',
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = 'translateY(-2px)';
                                    e.currentTarget.style.background = 'rgba(255,255,255,0.12)';
                                    e.currentTarget.style.borderColor = 'rgba(255,255,255,0.85)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.background = 'transparent';
                                    e.currentTarget.style.borderColor = 'rgba(255,255,255,0.55)';
                                }}
                            >
                                <CrownOutlined /> View Premium Plans
                            </Link>
                        </Space>
                    </AnimatedSection>
                </div>
            </div>

            <LandingFooter />
            <BiodataTourHint />
        </div>
    );
};

export default Landing;
